import React, { useEffect, useMemo, useRef, useState } from 'react';
import { GoogleMap, LoadScript, Marker, MarkerClusterer } from '@react-google-maps/api';
import AdminEditLeadModal from './AdminEditLeadModal';
import DuplicateReviewModal from './DuplicateReviewModal';

// PBFS palette (do not deviate)
const PBFS = {
  green: '#0DB14B',
  navy: '#00314F',
  blue: '#235EAC',
  gray: '#939598'
};

// PBFS-themed dark map styling (consistent with app look)
const PBFS_DARK_MAP_STYLE = [
  { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: PBFS.navy }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#475569' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#334155' }] },
  { featureType: 'poi', elementType: 'geometry', stylers: [{ color: '#2f4f3a' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#1a4d2e' }] },
  { featureType: 'all', elementType: 'labels.text.fill', stylers: [{ color: '#e2e8f0' }] },
  { featureType: 'all', elementType: 'labels.text.stroke', stylers: [{ color: '#000000' }, { weight: 2 }] }
];

const DEFAULT_CENTER = { lat: 39.5, lng: -98.35 }; // Continental US center

function toDateRangeDays(dateRange) {
  if (dateRange === '7d') return 7;
  if (dateRange === '30d') return 30;
  if (dateRange === '90d') return 90;
  return 0; // all-time
}

function normalizeStatus(raw) {
  const s = (raw || '').toLowerCase().trim();
  if (s === 'approved') return 'approved';
  if (s === 'rejected') return 'rejected';
  if (['pending', 'analyzed', 'needs_manual_review', 'new'].includes(s)) return 'pending';
  return 'pending';
}

function normalizeDupKey(sub) {
  const street = (sub.site_street_address || sub.business_address || '').toLowerCase().trim();
  const city = (sub.site_city || '').toLowerCase().trim();
  const state = (sub.site_state || '').toLowerCase().trim();
  const zip = (sub.site_zip || '').toLowerCase().trim();
  const byAddr = [street, city, state, zip].filter(Boolean).join('|');
  if (byAddr) return `addr:${byAddr}`;
  const lat = Number(sub.map_latitude);
  const lng = Number(sub.map_longitude);
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `gps:${lat.toFixed(4)},${lng.toFixed(4)}`;
  }
  return `unknown:${sub._id || sub.id || ''}`;
}

function normalizeBusinessName(raw) {
  const s = (raw || '').toLowerCase().trim();
  if (!s) return { raw: '', tokens: [] };
  const cleaned = s
    .replace(/['"]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const stop = new Set([
    'the', 'and', 'of', 'at', 'in',
    'llc', 'inc', 'ltd', 'co', 'company', 'corp', 'corporation',
    'services', 'service', 'group', 'center',
  ]);
  const tokens = cleaned
    .split(' ')
    .filter(Boolean)
    .filter(t => t.length > 1 && !stop.has(t));
  return { raw: cleaned, tokens };
}

function jaccardSimilarity(aTokens, bTokens) {
  if (!aTokens?.length || !bTokens?.length) return 0;
  const a = new Set(aTokens);
  const b = new Set(bTokens);
  let inter = 0;
  a.forEach(t => { if (b.has(t)) inter += 1; });
  const union = a.size + b.size - inter;
  return union === 0 ? 0 : inter / union;
}

function haversineMeters(aLat, aLng, bLat, bLng) {
  const toRad = (x) => (x * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

function normalizeAddrKey(sub) {
  const street = (sub.site_street_address || sub.business_address || '').toLowerCase().trim();
  const city = (sub.site_city || '').toLowerCase().trim();
  const state = (sub.site_state || '').toLowerCase().trim();
  const zip = (sub.site_zip || '').toLowerCase().trim();
  const byAddr = [street, city, state, zip].filter(Boolean).join('|');
  return byAddr || '';
}

function pinSvg({ fill, stroke, badgeText }) {
  const badge = badgeText
    ? `<circle cx="36" cy="14" r="10" fill="${PBFS.blue}" stroke="#ffffff" stroke-width="2"/>
       <text x="36" y="18" text-anchor="middle" font-family="system-ui, -apple-system, Segoe UI, Roboto" font-size="11" font-weight="700" fill="#ffffff">${badgeText}</text>`
    : '';

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="58" viewBox="0 0 44 58">
      <path d="M22 1 C12 1 4 9 4 19 C4 33 22 56 22 56 S40 33 40 19 C40 9 32 1 22 1 Z"
            fill="${fill}" stroke="${stroke}" stroke-width="3"/>
      <circle cx="22" cy="19" r="7.5" fill="#ffffff"/>
      <circle cx="22" cy="19" r="3.5" fill="${PBFS.navy}"/>
      ${badge}
    </svg>
  `)}`;
}

function officeSvg() {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="44" height="58" viewBox="0 0 44 58">
      <path d="M22 1 C12 1 4 9 4 19 C4 33 22 56 22 56 S40 33 40 19 C40 9 32 1 22 1 Z"
            fill="${PBFS.blue}" stroke="#ffffff" stroke-width="3"/>
      <rect x="14" y="13" width="16" height="14" rx="2" fill="#ffffff"/>
      <rect x="17" y="16" width="4" height="4" fill="${PBFS.navy}"/>
      <rect x="23" y="16" width="4" height="4" fill="${PBFS.navy}"/>
      <rect x="17" y="22" width="4" height="4" fill="${PBFS.navy}"/>
      <rect x="23" y="22" width="4" height="4" fill="${PBFS.navy}"/>
    </svg>
  `)}`;
}

export default function AnalyticsMap({ backendUrl, dateRange = '30d' }) {
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [offices, setOffices] = useState([]);
  const [selected, setSelected] = useState(null);
  const mapRef = useRef(null);
  const viewInitRef = useRef(false);
  const [mapView, setMapView] = useState(null); // { center: {lat,lng}, zoom } (set once; no continuous updates)

  const [layers, setLayers] = useState({
    offices: true,
    approved: true,
    pending: true,
    rejected: false,
    showDuplicatesBadge: true
  });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingInspection, setEditingInspection] = useState(null);
  const [dupConfig, setDupConfig] = useState({
    radiusM: 75,
    minNameSimilarity: 0.6
  });

  // Duplicate Review Modal state
  const [dupReviewOpen, setDupReviewOpen] = useState(false);
  const [dupReviewSeed, setDupReviewSeed] = useState(null);
  const [dupReviewGroup, setDupReviewGroup] = useState([]);
  const [dupReviewReason, setDupReviewReason] = useState('');
  
  // 🆕 GPS Cluster modal state
  const [gpsClusterModalOpen, setGpsClusterModalOpen] = useState(false);
  const [selectedCluster, setSelectedCluster] = useState(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const days = toDateRangeDays(dateRange);

        // Map submissions (minimal payload for map)
        const subsUrl = `${backendUrl}/api/admin/map/submissions?days=${days}&status=all&limit=5000&skip=0`;
        console.log('🗺️ AnalyticsMap: Fetching submissions from:', subsUrl);
        const token = localStorage.getItem('token');
        const subsRes = await fetch(subsUrl, { 
          headers: { 
            'X-Local-Dev': 'true',
            ...(token && { 'Authorization': `Bearer ${token}` })
          } 
        });
        const subsJson = await subsRes.json();
        if (subsRes.ok && subsJson?.success) {
          console.log('🗺️ AnalyticsMap: Loaded', subsJson.submissions?.length || 0, 'submissions');
          setSubmissions(subsJson.submissions || []);
        } else {
          console.warn('🗺️ AnalyticsMap: Submissions fetch failed:', subsRes.status, subsJson);
          setSubmissions([]);
        }

        // PBFS offices layer
        const offUrl = `${backendUrl}/api/admin/map/offices`;
        console.log('🗺️ AnalyticsMap: Fetching offices from:', offUrl);
        const offToken = localStorage.getItem('token');
        const offRes = await fetch(offUrl, { 
          headers: { 
            'X-Local-Dev': 'true',
            ...(offToken && { 'Authorization': `Bearer ${offToken}` })
          } 
        });
        const offJson = await offRes.json();
        if (offRes.ok && offJson?.success) {
          console.log('🗺️ AnalyticsMap: Loaded', offJson.offices?.length || 0, 'offices');
          setOffices(offJson.offices || []);
        } else {
          console.warn('🗺️ AnalyticsMap: Offices fetch failed:', offRes.status, offJson);
          setOffices([]);
        }
      } catch (e) {
        console.error('❌ AnalyticsMap load failed:', e);
        setSubmissions([]);
        setOffices([]);
      } finally {
        setLoading(false);
      }
    };

    // Note: backendUrl can be empty string '' for relative URLs (web app) - that's valid!
    // Only skip if backendUrl is explicitly undefined
    if (backendUrl !== undefined) {
      console.log('🗺️ AnalyticsMap: Loading map data...', { backendUrl, dateRange });
      load();
    }
  }, [backendUrl, dateRange]);

  const dupCounts = useMemo(() => {
    const counts = new Map();
    submissions.forEach((s) => {
      const key = normalizeDupKey(s);
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [submissions]);

  const filtered = useMemo(() => {
    return submissions.filter((s) => {
      const status = normalizeStatus(s.status || s.approval_status);
      if (status === 'approved' && !layers.approved) return false;
      if (status === 'pending' && !layers.pending) return false;
      if (status === 'rejected' && !layers.rejected) return false;
      return true;
    });
  }, [submissions, layers.approved, layers.pending, layers.rejected]);

  // 🆕 Group submissions by exact GPS coordinates (fixes overlapping pins)
  const gpsClusters = useMemo(() => {
    const clusters = new Map();
    
    filtered.forEach((submission) => {
      const lat = Number(submission.map_latitude);
      const lng = Number(submission.map_longitude);
      
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      
      // Create key with exact GPS coordinates (6 decimal places = ~0.1m precision)
      const gpsKey = `${lat.toFixed(6)},${lng.toFixed(6)}`;
      
      if (!clusters.has(gpsKey)) {
        clusters.set(gpsKey, {
          lat,
          lng,
          submissions: [],
          count: 0
        });
      }
      
      const cluster = clusters.get(gpsKey);
      cluster.submissions.push(submission);
      cluster.count = cluster.submissions.length;
    });
    
    return Array.from(clusters.values());
  }, [filtered]);

  const counts = useMemo(() => {
    const out = { approved: 0, pending: 0, rejected: 0, duplicates: 0 };
    submissions.forEach((s) => {
      out[normalizeStatus(s.status || s.approval_status)] += 1;
      if ((dupCounts.get(normalizeDupKey(s)) || 0) > 1) out.duplicates += 1;
    });
    return out;
  }, [submissions, dupCounts]);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: false,
    zoomControl: true,
    streetViewControl: false,
    mapTypeControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy', // smoother click+drag and trackpad behavior
    clickableIcons: false,
    styles: PBFS_DARK_MAP_STYLE
  }), []);

  const initialCenter = useMemo(() => {
    const first = filtered.find((s) => Number.isFinite(Number(s.map_latitude)) && Number.isFinite(Number(s.map_longitude)));
    if (!first) return DEFAULT_CENTER;
    return { lat: Number(first.map_latitude), lng: Number(first.map_longitude) };
  }, [filtered]);

  // Initialize view ONCE per mount / data load, but do not fight user panning/zooming afterward.
  useEffect(() => {
    if (viewInitRef.current) return;
    // Only initialize when we actually have data to focus.
    if (!filtered || filtered.length === 0) return;
    setMapView((prev) => prev || { center: initialCenter, zoom: 6 });
    viewInitRef.current = true;
  }, [filtered, initialCenter]);

  // 🆕 Helper to get cluster icon based on count and status
  const getClusterIcon = (cluster) => {
    // Determine cluster status (use most common status in cluster)
    const statusCounts = { approved: 0, pending: 0, rejected: 0 };
    cluster.submissions.forEach((s) => {
      const status = normalizeStatus(s.status || s.approval_status);
      statusCounts[status]++;
    });
    
    const dominantStatus = Object.keys(statusCounts).reduce((a, b) =>
      statusCounts[a] > statusCounts[b] ? a : b
    );
    
    // Check if any submissions are marked as duplicates
    const hasDuplicates = cluster.submissions.some((s) => {
      const persisted = (s.duplicate_status && s.duplicate_status !== 'none') ? s.duplicate_status : 'none';
      return persisted === 'canonical' || persisted === 'confirmed_duplicate';
    });
    
    // Badge text: show count if > 1, or 'D' if duplicate
    let badgeText = '';
    if (cluster.count > 1) {
      badgeText = cluster.count > 99 ? '99+' : cluster.count.toString();
    } else if (layers.showDuplicatesBadge && hasDuplicates) {
      badgeText = 'D';
    }
    
    // Determine color based on dominant status AND sales status
    let fill = '#FFC107'; // pending default
    if (dominantStatus === 'approved') {
      // Check if any submissions have sales status for color override
      const salesStatuses = cluster.submissions
        .map(s => s.sales_status)
        .filter(Boolean);
      
      if (salesStatuses.includes('won')) {
        fill = '#10B981'; // Bright green for won
      } else if (salesStatuses.includes('lost')) {
        fill = '#EF4444'; // Red for lost
      } else if (salesStatuses.includes('quoting')) {
        fill = '#8B5CF6'; // Purple for quoting
      } else if (salesStatuses.includes('contacted')) {
        fill = '#F59E0B'; // Orange for contacted
      } else if (salesStatuses.includes('distributed')) {
        fill = PBFS.blue; // Blue for distributed
      } else {
        fill = PBFS.green; // Default green for approved
      }
    } else if (dominantStatus === 'rejected') {
      fill = PBFS.gray;
    }
    
    return pinSvg({ fill, stroke: '#ffffff', badgeText });
  };

  const openEdit = (inspection) => {
    setEditingInspection(inspection);
    setIsEditOpen(true);
  };

  // Open the duplicate review modal with suspected duplicates
  const openDuplicateReview = (seed) => {
    const { members, reason } = getSuspectedGroup(seed);
    if (!members || members.length < 2) {
      alert('No suspected duplicates found for this pin under current detection rules.\n\nTip: Try adjusting the detection radius or name similarity threshold.');
      return;
    }
    setDupReviewSeed(seed);
    setDupReviewGroup(members);
    setDupReviewReason(reason);
    setDupReviewOpen(true);
  };

  // Handle when duplicates are confirmed via the modal
  const handleDuplicatesConfirmed = ({ canonicalId, duplicateIds, groupId }) => {
    // Update local state to reflect the confirmed duplicates
    setSubmissions((prev) => prev.map((s) => {
      const sid = s._id || s.id;
      if (sid === canonicalId) {
        return { ...s, duplicate_status: 'canonical', duplicate_group_id: groupId, duplicate_canonical_id: canonicalId };
      }
      if (duplicateIds.includes(sid)) {
        return { ...s, duplicate_status: 'confirmed_duplicate', duplicate_group_id: groupId, duplicate_canonical_id: canonicalId };
      }
      return s;
    }));
    setSelected(null);
    setDupReviewOpen(false);
  };

  // Handle when a record is marked as not-duplicate from the modal
  const handleMarkedNotDuplicate = (recordId) => {
    setSubmissions((prev) => prev.map((s) => 
      (s._id || s.id) === recordId 
        ? { ...s, duplicate_status: 'not_duplicate', duplicate_group_id: null, duplicate_canonical_id: null }
        : s
    ));
  };

  const rejectLead = async (inspection) => {
    const id = inspection?._id || inspection?.id;
    if (!id) return;
    const reason = window.prompt('Reason for rejection (optional):') || 'No reason provided';
    if (!window.confirm('Reject this tag lead?')) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/reject/${encodeURIComponent(id)}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ reason, rejected_by: 'Admin' })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.success) throw new Error(j?.detail || 'Reject failed');
      // Update local state (color + counters + selection)
      setSubmissions((prev) => prev.map((s) => ((s._id || s.id) === id ? { ...s, status: 'rejected', approval_status: 'rejected' } : s)));
      setSelected((prev) => (prev && ((prev._id || prev.id) === id) ? { ...prev, status: 'rejected', approval_status: 'rejected' } : prev));
    } catch (e) {
      alert(`❌ Reject failed: ${e.message || e}`);
    }
  };

  const getSuspectedGroup = (seed) => {
    if (!seed) return { members: [seed], reason: '' };
    const seedLat = Number(seed.map_latitude);
    const seedLng = Number(seed.map_longitude);
    if (!Number.isFinite(seedLat) || !Number.isFinite(seedLng)) return { members: [seed], reason: '' };

    const seedAddr = normalizeAddrKey(seed);
    const seedName = normalizeBusinessName(seed.business_name);

    const members = [];
    submissions.forEach((cand) => {
      const lat = Number(cand.map_latitude);
      const lng = Number(cand.map_longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      const d = haversineMeters(seedLat, seedLng, lat, lng);
      if (d > dupConfig.radiusM) return;

      const addr = normalizeAddrKey(cand);
      const addrMatch = !!seedAddr && !!addr && seedAddr === addr;

      const candName = normalizeBusinessName(cand.business_name);
      const sim = jaccardSimilarity(seedName.tokens, candName.tokens);
      const substringMatch =
        seedName.raw && candName.raw && (seedName.raw.includes(candName.raw) || candName.raw.includes(seedName.raw));
      const nameMatch = (sim >= dupConfig.minNameSimilarity) || substringMatch;

      // If both names exist but do NOT match, treat as not-duplicate unless address matches.
      const bothHaveNames = !!seedName.raw && !!candName.raw;
      if (bothHaveNames && !nameMatch && !addrMatch) return;

      // If names are missing, require address match to avoid nearby-business false positives.
      const anyMissingName = !seedName.raw || !candName.raw;
      if (anyMissingName && !addrMatch) return;

      members.push({ cand, d, sim });
    });

    // Stable sort: closest first
    members.sort((a, b) => a.d - b.d);
    const out = members.map(m => m.cand);

    const reason = seedAddr
      ? `Within ${dupConfig.radiusM}m + address match (and name similarity when present)`
      : `Within ${dupConfig.radiusM}m + business name similarity`;

    return { members: out, reason };
  };

  const markNotDuplicate = async (inspection) => {
    const id = inspection?._id || inspection?.id;
    if (!id) return;
    const note = window.prompt('Optional note: why is this not a duplicate?') || '';
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${backendUrl}/api/admin/duplicates/not-duplicate`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'X-Local-Dev': 'true',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({ inspection_id: id, note })
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok || !j?.success) throw new Error(j?.detail || 'Failed');
      setSubmissions((prev) => prev.map((s) => ((s._id || s.id) === id ? { ...s, duplicate_status: 'not_duplicate', duplicate_group_id: null, duplicate_canonical_id: null } : s)));
      setSelected((prev) => (prev && ((prev._id || prev.id) === id) ? { ...prev, duplicate_status: 'not_duplicate', duplicate_group_id: null, duplicate_canonical_id: null } : prev));
    } catch (e) {
      alert(`❌ Failed: ${e.message || e}`);
    }
  };

  return (
    <div className="bg-[var(--pbfs-navy)]/60 border border-white/10 rounded-xl overflow-hidden">
      <div className="p-5 border-b border-white/10 flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between">
        <div>
          <div className="text-xl font-bold text-white">🗺️ Analytics Map</div>
          <div className="text-white/60 text-sm">
            PBFS-themed map with layers • Click a pin to see details • {loading ? 'Loading…' : `${filtered.length.toLocaleString()} pins`}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80">
            <input type="checkbox" checked={layers.offices} onChange={(e) => setLayers((p) => ({ ...p, offices: e.target.checked }))} />
            Offices ({offices.length})
          </label>
          <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80">
            <input type="checkbox" checked={layers.approved} onChange={(e) => setLayers((p) => ({ ...p, approved: e.target.checked }))} />
            <span className="text-[var(--pbfs-green)] font-semibold">Approved</span> ({counts.approved})
          </label>
          <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80">
            <input type="checkbox" checked={layers.pending} onChange={(e) => setLayers((p) => ({ ...p, pending: e.target.checked }))} />
            <span className="text-yellow-300 font-semibold">Pending</span> ({counts.pending})
          </label>
          <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80">
            <input type="checkbox" checked={layers.rejected} onChange={(e) => setLayers((p) => ({ ...p, rejected: e.target.checked }))} />
            <span className="text-white/60 font-semibold">Rejected</span> ({counts.rejected})
          </label>
          <label className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-xs text-white/80">
            <input type="checkbox" checked={layers.showDuplicatesBadge} onChange={(e) => setLayers((p) => ({ ...p, showDuplicatesBadge: e.target.checked }))} />
            Duplicates badge (D)
          </label>
        </div>
      </div>
      
      {/* Sales Status Legend */}
      <div className="px-5 py-2 border-b border-white/10 bg-white/5">
        <div className="flex flex-wrap items-center gap-3 text-xs text-white/70">
          <span className="font-semibold text-white/50">Sales Status:</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full" style={{backgroundColor: PBFS.blue}}></span> Distributed</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-amber-500"></span> Contacted</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-purple-500"></span> Quoting</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-500"></span> Won</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-500"></span> Lost</span>
        </div>
      </div>

      <div className="relative">
        <div className="h-[520px] w-full">
          <LoadScript googleMapsApiKey={googleMapsApiKey}>
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapView?.center || initialCenter}
              zoom={mapView?.zoom || 6}
              options={mapOptions}
              onLoad={(map) => {
                mapRef.current = map;
              }}
            >
              {layers.offices && window.google?.maps && offices.map((o) => (
                <Marker
                  key={o.id}
                  position={{ lat: Number(o.latitude), lng: Number(o.longitude) }}
                  icon={{
                    url: officeSvg(),
                    scaledSize: new window.google.maps.Size(34, 44),
                    anchor: new window.google.maps.Point(17, 44)
                  }}
                  title={`${o.name} • ${o.full_address || ''}`}
                />
              ))}

              <MarkerClusterer>
                {(clusterer) => (
                  <>
                    {window.google?.maps && gpsClusters.map((cluster, idx) => (
                      <Marker
                        key={`cluster-${idx}-${cluster.lat}-${cluster.lng}`}
                        position={{ lat: cluster.lat, lng: cluster.lng }}
                        clusterer={clusterer}
                        icon={{
                          url: getClusterIcon(cluster),
                          scaledSize: new window.google.maps.Size(34, 44),
                          anchor: new window.google.maps.Point(17, 44)
                        }}
                        onClick={() => {
                          if (cluster.count === 1) {
                            // Single submission - show detail panel as before
                            setSelected(cluster.submissions[0]);
                          } else {
                            // Multiple submissions at same location - show cluster modal
                            setSelectedCluster(cluster);
                            setGpsClusterModalOpen(true);
                          }
                        }}
                        title={
                          cluster.count === 1
                            ? `${cluster.submissions[0].business_name || cluster.submissions[0].site_street_address || 'Tag Lead'} • ${normalizeStatus(cluster.submissions[0].status)}`
                            : `${cluster.count} submissions at this location`
                        }
                      />
                    ))}
                  </>
                )}
              </MarkerClusterer>
            </GoogleMap>
          </LoadScript>
        </div>

        {/* Details panel */}
        {selected && (
          <div className="absolute bottom-4 left-4 right-4 lg:left-6 lg:right-auto lg:w-[520px] bg-[var(--pbfs-navy)]/95 border border-white/10 rounded-2xl shadow-2xl p-4 backdrop-blur-sm">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-white font-bold truncate">
                  {selected.business_name || 'Unknown Business'}
                </div>
                <div className="text-white/60 text-sm truncate">
                  {[selected.site_street_address || selected.business_address, selected.site_city, selected.site_state, selected.site_zip].filter(Boolean).join(', ') || '—'}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  <span className="text-xs px-2 py-1 rounded bg-white/10 border border-white/10 text-white/80">
                    Status: <span className="font-semibold">{normalizeStatus(selected.status || selected.approval_status)}</span>
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-white/10 border border-white/10 text-white/80">
                    Tag ID: <span className="font-semibold">{selected._id || selected.id || '—'}</span>
                  </span>
                  {selected.duplicate_status && selected.duplicate_status !== 'none' && (
                    <span className="text-xs px-2 py-1 rounded bg-[var(--pbfs-blue)]/20 border border-[var(--pbfs-blue)]/40 text-[var(--pbfs-blue)] font-semibold">
                      {selected.duplicate_status === 'canonical' ? 'Canonical' : selected.duplicate_status === 'confirmed_duplicate' ? 'Confirmed duplicate' : selected.duplicate_status}
                    </span>
                  )}
                </div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-white/60 hover:text-white text-2xl leading-none"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="mt-3 flex gap-2">
              <button
                onClick={() => openEdit(selected)}
                className="flex-1 bg-[var(--pbfs-green)] hover:bg-[var(--pbfs-green)]/90 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
              >
                Open Lead (Edit)
              </button>
              <button
                onClick={() => openDuplicateReview(selected)}
                className="flex-1 bg-[var(--pbfs-blue)]/20 hover:bg-[var(--pbfs-blue)]/30 border border-[var(--pbfs-blue)]/40 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
                title="Review suspected duplicates side-by-side"
              >
                Review Duplicates
              </button>
              <button
                onClick={() => markNotDuplicate(selected)}
                className="flex-1 bg-white/10 hover:bg-white/20 border border-white/10 text-white/80 text-sm font-semibold py-2 rounded-lg transition-colors"
                title="Mark this record as not a duplicate"
              >
                Not Duplicate
              </button>
              <button
                onClick={() => rejectLead(selected)}
                className="flex-1 bg-red-500/20 hover:bg-red-500/30 border border-red-500/40 text-red-200 text-sm font-semibold py-2 rounded-lg transition-colors"
                title="Reject this lead"
              >
                Reject
              </button>
              {/* Removed Tag Leads and Google Maps buttons - clutter the workflow */}
            </div>
          </div>
        )}
      </div>

      <AdminEditLeadModal
        isOpen={isEditOpen}
        inspection={editingInspection}
        backendUrl={backendUrl}
        onClose={() => {
          setIsEditOpen(false);
          setEditingInspection(null);
        }}
        onSaved={(updated) => {
          // Keep map in sync with edits (best-effort)
          setSubmissions((prev) => prev.map((s) => (s._id === updated._id ? { ...s, ...updated } : s)));
          setSelected((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated } : prev));
          setIsEditOpen(false);
          setEditingInspection(null);
        }}
      />

      <DuplicateReviewModal
        isOpen={dupReviewOpen}
        onClose={() => {
          setDupReviewOpen(false);
          setDupReviewSeed(null);
          setDupReviewGroup([]);
        }}
        seedRecord={dupReviewSeed}
        duplicateGroup={dupReviewGroup}
        reason={dupReviewReason}
        backendUrl={backendUrl}
        onConfirmed={handleDuplicatesConfirmed}
        onMarkedNotDuplicate={handleMarkedNotDuplicate}
      />
      
      {/* 🆕 GPS Cluster Modal - Shows all submissions at the same location */}
      {gpsClusterModalOpen && selectedCluster && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100000] flex items-center justify-center p-4 pt-20 pb-6"
             onClick={() => {
               console.log('🗺️ GPS Cluster Modal: Backdrop clicked, closing modal');
               setGpsClusterModalOpen(false);
               setSelectedCluster(null);
             }}>
          <div className="bg-[var(--pbfs-navy)] rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl border border-white/10"
               onClick={(e) => {
                 console.log('🗺️ GPS Cluster Modal: Content clicked, preventing close');
                 e.stopPropagation();
               }}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[var(--pbfs-navy)] to-[var(--pbfs-blue)] text-white p-5 flex items-center justify-between border-b border-white/10">
              <div>
                <h2 className="text-xl font-bold">📍 {selectedCluster.count} Submissions at This Location</h2>
                <p className="text-white/70 text-sm mt-1">
                  GPS: {selectedCluster.lat.toFixed(6)}, {selectedCluster.lng.toFixed(6)}
                </p>
              </div>
              <button
                onClick={() => {
                  console.log('🗺️ GPS Cluster Modal: Close button clicked');
                  setGpsClusterModalOpen(false);
                  setSelectedCluster(null);
                }}
                className="text-white/70 hover:text-white text-2xl leading-none transition-colors w-8 h-8 flex items-center justify-center hover:bg-white/10 rounded-full"
              >
                ×
              </button>
            </div>

            {/* Submissions List */}
            <div className="p-4 overflow-y-auto max-h-[calc(80vh-120px)]">
              <div className="space-y-3">
                {selectedCluster.submissions.map((submission) => {
                  const status = normalizeStatus(submission.status || submission.approval_status);
                  const statusColor = status === 'approved' ? 'bg-green-100 text-green-800' : 
                                     status === 'rejected' ? 'bg-red-100 text-red-800' : 
                                     'bg-yellow-100 text-yellow-800';
                  
                  return (
                    <div
                      key={submission._id || submission.id}
                      className="bg-white/5 hover:bg-white/10 rounded-lg p-4 border border-white/10 transition-all cursor-pointer"
                      onClick={() => {
                        setSelected(submission);
                        setGpsClusterModalOpen(false);
                      }}
                    >
                      <div className="flex items-start gap-3">
                        {/* Thumbnail */}
                        {submission.image_url && (
                          <img
                            src={`${backendUrl}${submission.image_url}`}
                            alt="Tag"
                            className="w-16 h-16 rounded-lg object-cover border-2 border-white/20"
                          />
                        )}
                        
                        {/* Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-semibold text-white text-sm truncate">
                              {submission.business_name || submission.site_street_address || 'Unknown Location'}
                            </h3>
                            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${statusColor}`}>
                              {status}
                            </span>
                          </div>
                          
                          <p className="text-white/60 text-xs mt-1">
                            {submission.site_street_address || submission.business_address || 'No address'}
                          </p>
                          
                          <div className="flex items-center gap-4 mt-2 text-white/50 text-xs">
                            <span>👤 {submission.submitted_by || 'Unknown'}</span>
                            <span>📅 {new Date(submission.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/10 flex justify-between items-center gap-3">
              {/* Left: Review Duplicates Button (if multiple submissions) */}
              {selectedCluster.count > 1 && (
                <button
                  onClick={() => {
                    console.log('🔄 Auto-opening duplicate review for cluster:', selectedCluster);
                    // Use the first submission as seed and pass all submissions as group
                    setDupReviewSeed(selectedCluster.submissions[0]);
                    setDupReviewGroup(selectedCluster.submissions);
                    setDupReviewReason(`GPS Cluster: ${selectedCluster.count} submissions at same location`);
                    setDupReviewOpen(true);
                    setGpsClusterModalOpen(false); // Close cluster modal
                  }}
                  className="px-4 py-2 bg-[var(--pbfs-blue)] hover:bg-[var(--pbfs-blue)]/80 text-white rounded-lg transition-all text-sm font-semibold flex items-center gap-2"
                >
                  <span>🔄</span>
                  <span>Review {selectedCluster.count} Submissions as Duplicates</span>
                </button>
              )}
              
              {/* Right: Close Button */}
              <button
                onClick={() => setGpsClusterModalOpen(false)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/10 text-white rounded-lg transition-colors text-sm font-semibold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


